import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Landmark, AlertTriangle, CalendarClock, Ban } from "lucide-react";
import { CHEQUE_TONE } from "@/admin/ChequeDialog";

const FILTERS = ["open", "pending", "bounced", "cleared", "all"];

export default function Cheques() {
  const { user } = useAuth();
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get(`/admin/cheques?status=${status}`).then((r) => setData(r.data)), [status]);
  useEffect(() => { setData(null); load(); }, [load]);

  const act = async (c, path, body, ok) => {
    setBusy(c.id);
    try {
      await api.post(`/admin/cheques/${c.id}/${path}`, body);
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader title="Post-dated Cheques" subtitle="Cheques received against invoices. Present matured cheques to the bank, then mark them cleared (posts the receipt) or bounced (invoice stays open, agent is notified)." />
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Pending cheques" value={data.summary.pending} sub={money(data.summary.pending_value)} icon={Landmark} />
          <StatCard label="Matured - present now" value={data.summary.matured} sub="cheque date reached" icon={CalendarClock} accent />
          <StatCard label="Maturing in 3 days" value={data.summary.maturing_soon} sub="prepare for deposit" icon={CalendarClock} />
          <StatCard label="Bounced" value={data.summary.bounced} sub="follow up for replacement" icon={AlertTriangle} />
        </div>
      )}
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => <Button key={f} size="sm" variant={status === f ? "default" : "outline"} className="capitalize" onClick={() => setStatus(f)} data-testid={`cheque-filter-${f}`}>{f}</Button>)}
      </div>
      {!data ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : data.items.length === 0 ? <Empty title="No cheques" subtitle="Record a post-dated cheque from an invoice row on the Invoices page (PDC button)." />
          : (
            <Card className="overflow-hidden" data-testid="cheques-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left font-medium px-4 py-3">Cheque</th>
                      <th className="text-left font-medium px-4 py-3">Invoice</th>
                      <th className="text-left font-medium px-4 py-3">Customer / Agent</th>
                      <th className="text-left font-medium px-4 py-3">Cheque date</th>
                      <th className="text-right font-medium px-4 py-3">Amount</th>
                      <th className="text-left font-medium px-4 py-3">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((c) => (
                      <tr key={c.id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`cheque-item-${c.id}`}>
                        <td className="px-4 py-3 font-mono text-xs">#{c.cheque_no}<div className="text-muted-foreground">{c.bank || "—"}</div></td>
                        <td className="px-4 py-3 font-mono text-xs"><Link to={`/invoice/${c.order_id}`} className="text-primary hover:underline">{c.invoice_no}</Link></td>
                        <td className="px-4 py-3">{c.customer_name}<div className="text-xs text-muted-foreground">{c.agent}</div></td>
                        <td className="px-4 py-3">
                          {c.cheque_date}
                          {c.status === "pending" && (c.matured ? <Badge variant="destructive" className="ml-2">matured</Badge> : c.maturing_soon ? <Badge variant="secondary" className="ml-2">in {c.days_to_maturity}d</Badge> : null)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{money(c.amount)}</td>
                        <td className="px-4 py-3"><Badge variant={CHEQUE_TONE[c.status]} className="capitalize">{c.status}</Badge>{c.bounce_reason && <div className="text-xs text-muted-foreground">{c.bounce_reason}</div>}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {c.status === "pending" && (
                            <>
                              <Button size="sm" disabled={busy === c.id} onClick={() => act(c, "clear", { date: new Date().toISOString().slice(0, 10) }, "Cheque cleared - receipt posted")} data-testid={`clear-cheque-${c.id}`}>Cleared</Button>
                              <Button size="sm" variant="destructive" className="ml-1" disabled={busy === c.id} onClick={() => { const r = window.prompt("Bounce reason"); if (r !== null) act(c, "bounce", { reason: r }, "Marked bounced - agent notified"); }} data-testid={`bounce-cheque-${c.id}`}>Bounced</Button>
                            </>
                          )}
                          {c.status !== "cleared" && c.status !== "cancelled" && user?.role === "admin" && (
                            <Button size="sm" variant="ghost" className="ml-1" disabled={busy === c.id} onClick={() => window.confirm("Cancel this cheque record?") && act(c, "cancel", {}, "Cheque cancelled")} data-testid={`cancel-cheque-${c.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
    </div>
  );
}
